import { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { RefreshCw } from "lucide-react";
import { admin } from "../../services/adminApi";
import { api } from "../../services/api";

export default function AdminMetricDetail() {
  const { type } = useParams(); // "users" | "projects" | "subscriptions"
  const [sp, setSp] = useSearchParams();
  const scope = sp.get("scope") || (type === "users" ? "active" : type === "projects" ? "active" : "active");
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);

  async function load() {
    setLoading(true);
    try {
      if (type === "users") {
        const r = await api.get(`/api/admin/metrics/users-list?scope=${encodeURIComponent(scope)}`);
        setRows(r.items || []);
      } else if (type === "projects") {
        const r = await api.get(`/api/admin/metrics/projects-list?status=${encodeURIComponent(scope)}`);
        setRows(r.items || []);
      } else if (type === "subscriptions") {
        const r = await api.get(`/api/admin/metrics/subscriptions-list?status=${encodeURIComponent(scope)}`);
        setRows(r.items || []);
      }
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [type, scope]);

  const title = useMemo(() => {
    if (type === "users") return "Users";
    if (type === "projects") return "Projects";
    if (type === "subscriptions") return "Subscriptions";
    return "Details";
  }, [type]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{title} — Details</h1>
          <p className="text-gray-600 mt-1">
            Scope: <strong>{scope}</strong>
          </p>
        </div>
        <button onClick={load} className="p-2 bg-white border rounded-md">
          <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
        </button>
      </div>

      {/* Scope switcher */}
      <div className="flex gap-2">
        {type === "users" && ["active", "new30d", "all"].map((s) => (
          <ScopeChip key={s} active={scope===s} onClick={() => setSp({ scope: s })}>{s}</ScopeChip>
        ))}
        {type === "projects" && ["active", "archived", "all"].map((s) => (
          <ScopeChip key={s} active={scope===s} onClick={() => setSp({ scope: s })}>{s}</ScopeChip>
        ))}
        {type === "subscriptions" && ["active", "trialing", "all"].map((s) => (
          <ScopeChip key={s} active={scope===s} onClick={() => setSp({ scope: s })}>{s}</ScopeChip>
        ))}
      </div>

      {/* Tables */}
      {type === "users" && <UsersTable rows={rows} loading={loading} />}
      {type === "projects" && <ProjectsTable rows={rows} loading={loading} />}
      {type === "subscriptions" && <SubsTable rows={rows} loading={loading} />}
    </div>
  );
}

function ScopeChip({ active, children, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`px-3 py-1.5 rounded-full text-sm border ${active ? "bg-black text-white border-black" : "bg-white text-gray-700 hover:bg-gray-50"}`}
    >
      {children}
    </button>
  );
}

function UsersTable({ rows, loading }) {
  if (loading) return <div>Loading…</div>;
  if (!rows.length) return <div className="text-gray-500">No users.</div>;
  return (
    <div className="overflow-x-auto bg-white border rounded-md">
      <table className="w-full">
        <thead className="bg-gray-50">
          <tr>
            <Th>Name</Th><Th>Email</Th><Th>Role</Th><Th>Plan</Th><Th>Last Login</Th><Th>Created</Th>
          </tr>
        </thead>
        <tbody className="divide-y">
          {rows.map((u) => (
            <tr key={u.id} className="hover:bg-gray-50">
              <Td>{u.name}</Td>
              <Td>{u.email}</Td>
              <Td>{u.role}</Td>
              <Td>{u.plan}</Td>
              <Td>{u.lastLoginAt ? new Date(u.lastLoginAt).toLocaleString() : "—"}</Td>
              <Td>{new Date(u.createdAt).toLocaleDateString()}</Td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ProjectsTable({ rows, loading }) {
  if (loading) return <div>Loading…</div>;
  if (!rows.length) return <div className="text-gray-500">No projects.</div>;
  return (
    <div className="overflow-x-auto bg-white border rounded-md">
      <table className="w-full">
        <thead className="bg-gray-50">
          <tr>
            <Th>Name</Th><Th>Status</Th><Th>Owner</Th><Th>Updated</Th><Th>Created</Th>
          </tr>
        </thead>
        <tbody className="divide-y">
          {rows.map((p) => (
            <tr key={p.id} className="hover:bg-gray-50">
              <Td><Link to={`/admin/projects?q=${encodeURIComponent(p.name)}`} className="text-blue-600 hover:underline">{p.name || p.id}</Link></Td>
              <Td className="capitalize">{p.status}</Td>
              <Td>{p.ownerEmail || "—"}</Td>
              <Td>{new Date(p.updatedAt).toLocaleString()}</Td>
              <Td>{new Date(p.createdAt).toLocaleDateString()}</Td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SubsTable({ rows, loading }) {
  if (loading) return <div>Loading…</div>;
  if (!rows.length) return <div className="text-gray-500">No subscriptions.</div>;
  return (
    <div className="overflow-x-auto bg-white border rounded-md">
      <table className="w-full">
        <thead className="bg-gray-50">
          <tr>
            <Th>User</Th><Th>Plan</Th><Th>Status</Th><Th>Updated</Th>
          </tr>
        </thead>
        <tbody className="divide-y">
          {rows.map((s) => (
            <tr key={s.id} className="hover:bg-gray-50">
              <Td>{s.userEmail}</Td>
              <Td>{s.plan}</Td>
              <Td className="capitalize">{s.status}</Td>
              <Td>{new Date(s.updatedAt).toLocaleString()}</Td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Th({ children }) { return <th className="text-left text-xs font-semibold text-gray-600 uppercase tracking-wide px-3 py-2">{children}</th>; }
function Td({ children }) { return <td className="px-3 py-2 text-sm text-gray-900">{children}</td>; }
